/**
 * @logue/reverb
 *
 * @description JavaScript Reverb effect class
 * @author Logue <logue@hotmail.co.jp>
 * @copyright 2019-2025 By Masashi Yoshikawa All rights reserved.
 * @license MIT
 * @version 1.3.16
 * @see {@link https://github.com/logue/Reverb.js}
 */

import { white as c, violet as m, red as f, pink as b, green as N, blue as g } from "@thi.ng/colored-noise";
import { take as v } from "@thi.ng/transducers";
import { SYSTEM as w } from "@thi.ng/random";
const d = {
  version: "1.3.16",
  date: "2025-03-30T16:28:39.195Z"
}, o = {
  /** Blue noise */
  blue: "blue",
  /** Brown noise (same as red noise) */
  brown: "red",
  /** Green noise */
  green: "green",
  /** Pink noise */
  pink: "pink",
  /** Red noise */
  red: "red",
  /** Violet noise */
  violet: "violet"
}, y = {
  noise: "white",
  scale: 1,
  peaks: 2,
  randomAlgorithm: w,
  decay: 2,
  delay: 0,
  reverse: !1,
  time: 2,
  filterType: "allpass",
  filterFreq: 2200,
  filterQ: 1,
  mix: 0.5,
  once: !1
};
class s {
  /** Version strings */
  static version = d.version;
  /** Build date */
  static build = d.date;
  /** AudioContext */
  ctx;
  /** Wet Level (Reverberated node) */
  wetGainNode;
  /** Dry Level (Original sound node) */
  dryGainNode;
  /** Impulse response filter */
  filterNode;
  /** Convolution node for applying impulse response */
  convolverNode;
  /** Output gain node */
  outputNode;
  /** Option */
  options;
  /** Connected flag */
  isConnected;
  /** Noise Generator */
  noise = c;
  /**
   * Constructor
   *
   * @param ctx - Root AudioContext
   * @param options - Configure
   */
  constructor(e, t) {
    this.ctx = e, this.options = Object.assign(y, t), this.wetGainNode = this.ctx.createGain(), this.dryGainNode = this.ctx.createGain(), this.filterNode = this.ctx.createBiquadFilter(), this.convolverNode = this.ctx.createConvolver(), this.outputNode = this.ctx.createGain(), this.isConnected = !1, this.filterType(this.options.filterType), this.setNoise(this.options.noise), this.buildImpulse(), this.mix(this.options.mix);
  }
  /**
   * Connect the node for the reverb effect to the original sound node.
   *
   * @param sourceNode - Input source node
   */
  connect(e) {
    return this.isConnected && this.options.once ? (this.isConnected = !1, this.outputNode) : (this.convolverNode.connect(this.filterNode), this.filterNode.connect(this.wetGainNode), e.connect(this.convolverNode), e.connect(this.dryGainNode).connect(this.outputNode), e.connect(this.wetGainNode).connect(this.outputNode), this.isConnected = !0, this.outputNode);
  }
  /**
   * Disconnect the reverb node
   *
   * @param sourceNode - Input source node
   */
  disconnect(e) {
    return this.isConnected && (this.convolverNode.disconnect(this.filterNode), this.filterNode.disconnect(this.wetGainNode)), this.isConnected = !1, e;
  }
  /**
   * Dry/Wet ratio
   *
   * @param mix - Ratio (0~1)
   */
  mix(e) {
    if (!s.inRange(e, 0, 1))
      throw new RangeError("[Reverb.js] Dry/Wet ratio must be between 0 to 1.");
    this.options.mix = e, this.dryGainNode.gain.value = 1 - this.options.mix, this.wetGainNode.gain.value = this.options.mix;
  }
  /**
   * Set Impulse Response time length (second)
   *
   * @param value - IR length
   */
  time(e) {
    if (!s.inRange(e, 1, 50))
      throw new RangeError(
        "[Reverb.js] Time length of inpulse response must be less than 50sec."
      );
    this.options.time = e, this.buildImpulse();
  }
  /**
   * Impulse response decay rate.
   *
   * @param value - Decay value
   */
  decay(e) {
    if (!s.inRange(e, 0, 100))
      throw new RangeError(
        "[Reverb.js] Inpulse Response decay level must be less than 100."
      );
    this.options.decay = e, this.buildImpulse();
  }
  /**
   * Delay before reverberation starts
   *
   * @param value - Time[ms]
   */
  delay(e) {
    if (!s.inRange(e, 0, 100))
      throw new RangeError(
        "[Reverb.js] Inpulse Response delay time must be less than 100."
      );
    this.options.delay = e, this.buildImpulse();
  }
  /**
   * Reverse the impulse response.
   *
   * @param reverse - Reverse IR
   */
  reverse(e) {
    this.options.reverse = e, this.buildImpulse();
  }
  /**
   * Filter for impulse response
   *
   * @param type - Filiter Type
   */
  filterType(e = "allpass") {
    this.filterNode.type = this.options.filterType = e;
  }
  /**
   * Filter frequency applied to impulse response
   *
   * @param freq - Frequency
   */
  filterFreq(e) {
    if (!s.inRange(e, 20, 2e4))
      throw new RangeError(
        "[Reverb.js] Filter frequrncy must be between 20 and 20000."
      );
    this.options.filterFreq = e, this.filterNode.frequency.value = this.options.filterFreq;
  }
  /**
   * Filter quality.
   *
   * @param q - Quality
   */
  filterQ(e) {
    if (!s.inRange(e, 0, 10))
      throw new RangeError(
        "[Reverb.js] Filter Q value must be between 0 and 10."
      );
    this.options.filterQ = e, this.filterNode.Q.value = this.options.filterQ;
  }
  /**
   * set IR source noise peaks
   *
   * @param p - Peaks
   */
  peaks(e) {
    this.options.peaks = e, this.buildImpulse();
  }
  /**
   * set IR source noise scale.
   *
   * @param s - Scale
   */
  scale(e) {
    this.options.scale = e, this.buildImpulse();
  }
  /**
   * set IR source noise generator.
   *
   * @param a - Algorithm
   */
  randomAlgorithm(e) {
    this.options.randomAlgorithm = e, this.buildImpulse();
  }
  /**
   * Inpulse Response Noise algorithm.
   *
   * @param type - IR noise algorithm type.
   */
  setNoise(e) {
    switch (this.options.noise = e, e) {
      case o.blue:
        this.noise = g;
        break;
      case o.green:
        this.noise = N;
        break;
      case o.pink:
        this.noise = b;
        break;
      case o.red:
      case o.brown:
        this.noise = f;
        break;
      case o.violet:
        this.noise = m;
        break;
      default:
        this.noise = c;
    }
    this.buildImpulse();
  }
  /**
   * Set Random Algorythm
   *
   * @param algorithm - Algorythm
   */
  setRandomAlgorythm(e) {
    this.options.randomAlgorithm = e, this.buildImpulse();
  }
  /**
   * Return true if in range, otherwise false
   *
   * @param x - Target value
   * @param min - Minimum value
   * @param max - Maximum value
   */
  static inRange(e, t, n) {
    return (e - t) * (e - n) <= 0;
  }
  /** Utility function for building an impulse response from the module parameters. */
  buildImpulse() {
    const e = this.ctx.sampleRate, t = Math.max(e * this.options.time, 1), n = e * this.options.delay, a = this.ctx.createBuffer(2, t, e), l = new Float32Array(t), h = new Float32Array(t), p = this.getNoise(t), u = this.getNoise(t);
    for (let i = 0; i < t; i++) {
      let r = 0;
      i < n ? (l[i] = 0, h[i] = 0, r = this.options.reverse ?? !1 ? t - (i - n) : i - n) : r = this.options.reverse ?? !1 ? t - i : i, l[i] = (p[i] ?? 0) * (1 - r / t) ** this.options.decay, h[i] = (u[i] ?? 0) * (1 - r / t) ** this.options.decay;
    }
    a.getChannelData(0).set(l), a.getChannelData(1).set(h), this.convolverNode.buffer = a;
  }
  /**
   * Noise source
   *
   * @param duration - length of IR.
   */
  getNoise(e) {
    return [
      ...v(
        e,
        this.noise({
          bins: this.options.peaks,
          scale: this.options.scale,
          rnd: this.options.randomAlgorithm
        })
      )
    ];
  }
}
export {
  s as default
};
